import { createEffect, createResource, on, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { A, useNavigate } from "@solidjs/router"
import { useLanguage } from "@/context/language"
import { fileSyncErrorKey } from "@/datox/file-integrations"
import { Icon } from "@opencode-ai/ui/v2/icon"
import { MenuV2 } from "@opencode-ai/ui/v2/menu-v2"
import { AutomationApiError, automationErrorMessage, automationWorkflows, getWorkflowApplication, rerunPeriodWorkflow, runPeriodWorkflow, workflowRunHistoryPath } from "@/datox/automations"
import { showToast } from "@/utils/toast"

export function DatoxWorkflowRunAction(props: { projectID: string; periodID?: string; caseID?: string; disabled: boolean; hasFiles: boolean; hasSourceOutputs: boolean; uploading: boolean; filesLoading: boolean; onError: (message: string) => void }) {
  const language = useLanguage()
  const navigate = useNavigate()
  const [state, setState] = createStore({ busy: false, requestID: "", requestScope: "" })
  const [info, infoActions] = createResource(() => props.periodID ? { project: props.projectID, period: props.periodID, caseID: props.caseID } : false,
    (scope) => getWorkflowApplication(scope.project, scope.period, scope.caseID))
  const current = () => info.loading || info.error ? undefined : info.latest
  let disposed = false
  onCleanup(() => { disposed = true })
  createEffect(() => {
    if (!props.periodID) return
    const refresh = () => {
      if (!disposed && !info.loading && !state.busy) void Promise.resolve(infoActions.refetch()).catch(() => undefined)
    }
    window.addEventListener("focus", refresh)
    const timer = setInterval(() => { if (!document.hidden) refresh() }, 10_000)
    onCleanup(() => { window.removeEventListener("focus", refresh); clearInterval(timer) })
  })
  const linkedAutomation = () => {
    const item = current()?.application
    if (!item || props.caseID || item.project_id !== props.projectID || item.target_period_id !== props.periodID) return undefined
    return item
  }
  const latestRun = () => {
    const item = linkedAutomation()
    const latest = item?.latest_run
    if (!item || !latest || latest.automation_id !== item.id || latest.project_id !== props.projectID || latest.period_id !== props.periodID) return undefined
    return latest
  }
  createEffect(on(() => `${props.projectID}:${props.periodID}:${props.caseID ?? ""}`, () => { props.onError(""); setState({ requestID: "", requestScope: "" }) }))
  const run = async (action: "run" | "rerun" | "automation" = "run") => {
    const application = current()
    const rerun = action === "rerun"
    if (automationDisabled() || !application || !props.periodID) return
    if (action !== "automation" && runDisabled()) return
    if (rerun && rerunDisabled()) return
    const scope = { project: props.projectID, period: props.periodID, caseID: props.caseID }
    const unchanged = () => !disposed && props.projectID === scope.project && props.periodID === scope.period && props.caseID === scope.caseID
    props.onError("")
    setState("busy", true)
    try {
      if (action === "automation" && linkedAutomation()) {
        navigate(`/automations/${encodeURIComponent(linkedAutomation()!.id)}`)
        return
      }
      if (action === "automation" || !rerun && !application.application) {
        const query = new URLSearchParams({ apply: "1", project_id: scope.project })
        if (props.hasSourceOutputs || scope.caseID) {
          const source = (await automationWorkflows(scope.project)).find((w) => scope.caseID ? w.id === scope.caseID : w.kind === "period" && w.id === scope.period)
          if (source) { query.set("source_kind", source.kind); query.set("source_id", source.id) }
        } else query.set("target_period_id", scope.period)
        if (unchanged()) navigate(`/automations?${query}`)
        return
      }
      const key = `${scope.project}:${scope.period}:${rerun ? "rerun" : "automation"}`
      const requestID = state.requestScope === key && state.requestID ? state.requestID : crypto.randomUUID()
      setState({ requestID, requestScope: key })
      const result = await (rerun ? rerunPeriodWorkflow : runPeriodWorkflow)(scope.project, scope.period, requestID)
      if (unchanged()) {
        setState("requestID", "")
        if (rerun) showToast({ title: language.t("datox.workflow.run.rerunStarted") })
        navigate(rerun ? workflowRunHistoryPath(scope.project, scope.period, result.id) : `/automations/${encodeURIComponent(result.automation_id)}/runs/${encodeURIComponent(result.id)}`)
      }
    } catch (error) {
      const message = error instanceof AutomationApiError ? automationErrorMessage(error.detail) : ""
      if (unchanged() && (message === "file_sync_in_progress" || message === "file_sync_needs_attention")) {
        props.onError(language.t(fileSyncErrorKey(message)))
        return
      }
      if (unchanged()) props.onError(message || language.t(rerun ? "datox.workflow.runs.error" : "datox.automations.error"))
    } finally { if (!disposed) setState("busy", false) }
  }
  const needsFiles = () => Boolean(current()?.application) && !props.hasFiles
  const automationDisabled = () => props.disabled || state.busy || props.uploading || props.filesLoading || !current()?.can_write
  const runDisabled = () => automationDisabled() || needsFiles() || !!current()?.active_run
  const rerunDisabled = () => runDisabled() || !!props.caseID || !props.hasFiles || !current()?.can_rerun
  return <div class="workflow-run-action">
    <Show when={props.periodID && !props.caseID}>
      <A class="workflow-action" data-testid="workflow-run-history" href={workflowRunHistoryPath(props.projectID, props.periodID!)}>
        <Icon name="clock" size="small" />{language.t("datox.workflow.runs.title")}
      </A>
    </Show>
    <Show when={latestRun()}>{(latest) => <A class="workflow-action" data-testid="workflow-automation-latest-run"
        href={`/automations/${encodeURIComponent(latest().automation_id)}/runs/${encodeURIComponent(latest().id)}`}
        title={language.t("datox.automations.navigation.latestRun", { name: linkedAutomation()?.name ?? "" })}
        aria-label={language.t("datox.automations.navigation.latestRun", { name: linkedAutomation()?.name ?? "" })}>
        <Icon name="clock" size="small" />{language.t("datox.automations.run.latest")}
    </A>}</Show>
    <MenuV2 placement="bottom-end">
      <MenuV2.Trigger type="button" class="workflow-action" data-primary data-testid="workflow-run-menu" aria-busy={state.busy}
        disabled={runDisabled() && automationDisabled() && rerunDisabled()}>
        {language.t(state.busy ? "common.loading" : "datox.workflow.run.title")}
        <Icon name="chevron-down" size="small" />
      </MenuV2.Trigger>
      <MenuV2.Portal><MenuV2.Content class="min-w-[200px]">
        <MenuV2.Item data-testid="workflow-run" disabled={runDisabled()}
          title={needsFiles() ? language.t("datox.automations.manual.uploadFirst") : !current()?.application ? language.t("datox.automations.manual.configure") : undefined}
          onSelect={() => void run()}>
          <span class="flex items-center gap-2"><Icon name="branch" size="small" />{language.t("datox.workflow.run.title")}</span>
        </MenuV2.Item>
        <MenuV2.Item data-testid="workflow-rerun" disabled={rerunDisabled()}
          title={language.t(!current()?.can_rerun ? "datox.workflow.run.rerunFirst" : !props.hasFiles ? "datox.automations.manual.uploadFirst" : "datox.workflow.run.rerunDescription")}
          onSelect={() => void run("rerun")}>
          <span class="flex items-center gap-2"><Icon name="clock" size="small" />{language.t("datox.workflow.run.rerun")}</span>
        </MenuV2.Item>
        <MenuV2.Item data-testid="workflow-automation" disabled={automationDisabled()} onSelect={() => void run("automation")}>
          <span class="flex items-center gap-2"><Icon name="branch" size="small" />{language.t("datox.workflow.run.automation")}</span>
        </MenuV2.Item>
      </MenuV2.Content></MenuV2.Portal>
    </MenuV2>
  </div>
}
